<template>
    <NuxtLink :to="jobLink" class="job-card shrink-0 w-[300px] block cursor-pointer">
        <!-- Image -->
        <div class="relative h-[220px] rounded-t-3xl overflow-hidden bg-gray-200 dark:bg-surface-subtle">
            <CThumbnailImage
                v-if="job.imageUrl"
                :src="job.imageUrl"
                :alt="job.title"
                :type="job.imageType || 'job'"
                class="size-full"
            />
            <div v-else class="size-full flex items-center justify-center bg-gradient-to-br from-orange-100 to-orange-200 dark:from-orange-900/30 dark:to-orange-900/30">
                <span class="text-4xl">💼</span>
            </div>
            <!-- Match Score Badge -->
            <div
                v-if="job.matchScore"
                class="absolute top-3 right-3 bg-white/90 dark:bg-surface-card/90 backdrop-blur-sm rounded-full px-2.5 py-1 shadow-sm"
            >
                <span class="text-sm font-semibold text-blue-600 dark:text-blue-400">{{ job.matchScore }}%</span>
            </div>
        </div>

        <!-- Content -->
        <div class="bg-white dark:bg-surface-card p-4 rounded-b-3xl flex flex-col gap-3">
            <div class="flex flex-col gap-3">
                <!-- Title & Organization -->
                <div class="flex flex-col gap-1">
                    <h3 class="font-semibold text-lg text-[#1d191f] dark:text-white tracking-tight leading-normal line-clamp-2">
                        {{ job.title }}
                    </h3>
                    <p class="text-base text-[#6c5d73] dark:text-gray-300 leading-normal">
                        {{ job.organization }}
                    </p>
                </div>

                <!-- Description -->
                <p
                    v-if="description"
                    class="text-sm text-[#6c5d73] dark:text-gray-300 leading-snug line-clamp-2"
                >
                    {{ description }}
                </p>

                <!-- Location -->
                <p v-if="job.location" class="font-semibold text-sm text-[#1d191f] dark:text-white leading-5">
                    <span class="text-[#047274] dark:text-primary-400">#</span>{{ job.location }}
                </p>

                <!-- Tags -->
                <div class="flex flex-wrap gap-2">
                    <span
                        v-for="tag in job.tags"
                        :key="tag"
                        class="font-semibold text-sm text-[#1d191f] dark:text-white leading-5"
                    >
                        <span class="text-[#047274] dark:text-primary-400">#</span>{{ tag }}
                    </span>
                </div>
            </div>
        </div>
    </NuxtLink>
</template>

<script setup lang="ts">
import CThumbnailImage from '@/components/ui/CThumbnailImage.vue'

interface Job {
    title: string
    organization: string
    description?: string
    imageUrl?: string
    imageType?: string
    location?: string
    tags: string[]
    slug?: string
    id?: string
    matchScore?: number
}

const props = defineProps<{
    job: Job
}>()

function plainText(value?: string): string {
    return (value || '')
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ' ')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, ' ')
        .trim()
}

const description = computed(() => {
    const sourceDescription = plainText(props.job.description)
    if (sourceDescription) return sourceDescription

    if (props.job.tags.length) {
        return `Témy pozície: ${props.job.tags.slice(0, 3).join(', ')}.`
    }

    if (props.job.organization) {
        return `Pracovná príležitosť v organizácii ${props.job.organization}.`
    }

    return ''
})

const jobLink = computed(() => {
    return `/praca/${props.job.slug || props.job.id}`
})
</script>

<style scoped>
.job-card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.job-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}

.job-card :deep(.CThumbnailImage) {
    aspect-ratio: auto;
    border-radius: 0;
}
</style>
